import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/toaster";
import { authAPI } from "./services/api";
import PushPrompt from "./components/PushPrompt";

// Components
import LoginPage from "./components/LoginPage";
import OwnerDashboard from "./components/OwnerDashboard";
import AdminDashboard from "./components/AdminDashboard";
import CoordinatorDashboard from "./components/CoordinatorDashboard";
import ClientDashboard from "./components/ClientDashboard";
import SupervisorDashboard from "./components/SupervisorDashboard";
import OperatorDashboard from "./components/OperatorDashboard";
import AccountantDashboard from "./components/AccountantDashboard";

// Auth Context
const AuthContext = React.createContext();

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const savedUser = localStorage.getItem('user');
    
    console.log('[APP] Initial load - token exists:', !!token, 'user exists:', !!savedUser);
    
    if (token && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        console.log('[APP] Restoring user session:', userData.name, userData.role);
        setUser(userData);
      } catch (error) {
        console.error('[APP] Error parsing saved user:', error);
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  // Prevent accidental value changes when scrolling over number inputs.
  // Blurs the focused number field on wheel so values can only be typed.
  useEffect(() => {
    const handleWheel = () => {
      const el = document.activeElement;
      if (el && el.tagName === 'INPUT' && el.type === 'number') {
        el.blur();
      }
    };
    document.addEventListener('wheel', handleWheel, { passive: true });
    return () => document.removeEventListener('wheel', handleWheel);
  }, []);

  const login = async (credentials) => {
    console.log('[AUTH CONTEXT] Login called with:', { mobile: credentials.mobile, password: '***' });
    try {
      const response = await authAPI.login(credentials.mobile, credentials.password);
      console.log('[AUTH CONTEXT] Login response:', response);
      
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('user', JSON.stringify(response.user));
      console.log('[AUTH CONTEXT] Token saved to localStorage');
      
      setUser(response.user);
      console.log('[AUTH CONTEXT] User state updated');
      return response;
    } catch (error) {
      console.error('[AUTH CONTEXT] Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      setUser(null);
    }
  };

  const authValue = {
    user,
    login,
    logout,
    isAuthenticated: !!user
  };

  // Get dashboard component based on role
  const getDashboard = () => {
    if (!user) return <Navigate to="/login" />;
    
    switch (user.role) {
      case 'owner':
        return <OwnerDashboard />;
      case 'admin':
        return <AdminDashboard />;
      case 'coordinator':
        return <CoordinatorDashboard />;
      case 'client':
        return <ClientDashboard />;
      case 'supervisor':
        return <SupervisorDashboard />;
      case 'machine_operator':
      case 'cutting_operator':
      case 'boring_operator':
        return <OperatorDashboard />;
      case 'accountant':
        return <AccountantDashboard />;
      default:
        return <Navigate to="/login" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={authValue}>
      <div className="App min-h-screen bg-slate-50">
        <BrowserRouter>
          <Routes>
            <Route 
              path="/login" 
              element={!user ? <LoginPage /> : <Navigate to="/dashboard" />} 
            />
            <Route 
              path="/dashboard" 
              element={getDashboard()} 
            />
            <Route 
              path="/" 
              element={<Navigate to={user ? "/dashboard" : "/login"} />} 
            />
          </Routes>
        </BrowserRouter>
        <Toaster />
        {user && <PushPrompt />}
      </div>
    </AuthContext.Provider>
  );
}

export default App;
